import { useState, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  Plus,
  Workflow,
  Loader2,
  MoreHorizontal,
  XCircle,
  Globe,
  FileUp,
  ArrowLeft,
  X,
  Upload,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { useRuns, useCreateRun, useCancelRun, useConfigs } from '@/hooks/useTriplets'
import { uploadFiles, uploadComplete } from '@/lib/triplets-api'
import { useToast } from '@/hooks/use-toast'
import type { PipelineRunStatus, PipelineRunSummary } from '@/types/triplets'

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const statusConfig: Record<
  PipelineRunStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  pending: { label: 'Pending', variant: 'outline' },
  running: { label: 'Running', variant: 'default', className: 'bg-blue-600 animate-pulse' },
  completed: { label: 'Completed', variant: 'default', className: 'bg-green-600' },
  failed: { label: 'Failed', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
}

function StatusBadge({ status }: { status: PipelineRunStatus }) {
  const config = statusConfig[status] ?? { label: status, variant: 'outline' as const }
  return (
    <Badge variant={config.variant} className={config.className}>
      {config.label}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Wizard types
// ---------------------------------------------------------------------------

type SourceType = 'urls' | 'files'

interface WizardState {
  step: 1 | 2
  name: string
  config_id: string
  sourceType: SourceType | null
  scrape_urls: string
  files: File[]
  skip_steps: string[]
  dry_run: boolean
}

const STAGE_GROUPS: { label: string; stages: string[]; hideForFiles?: boolean }[] = [
  { label: 'Ingestion', stages: ['scrape'], hideForFiles: true },
  { label: 'Processing', stages: ['extract', 'build', 'enrich'] },
  { label: 'Enrichment', stages: ['policies', 'lookups'] },
  { label: 'Deployment', stages: ['upload', 'parse'] },
]

const STAGE_LABELS: Record<string, string> = {
  scrape: 'Scrape',
  extract: 'Extract',
  build: 'Build',
  enrich: 'Enrich',
  policies: 'Policies',
  lookups: 'Lookups',
  upload: 'Upload',
  parse: 'Parse',
}

const ACCEPTED_EXTENSIONS = '.pdf,.docx,.xlsx,.csv,.txt,.md,.json,.html,.htm,.xml'

const defaultWizard: WizardState = {
  step: 1,
  name: '',
  config_id: '',
  sourceType: null,
  scrape_urls: '',
  files: [],
  skip_steps: [],
  dry_run: false,
}

function generateDefaultName(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `run_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function TripletsPage() {
  const { data, isLoading, error } = useRuns()
  const { data: configsData } = useConfigs()
  const createRunMut = useCreateRun()
  const cancelRunMut = useCancelRun()
  const { toast } = useToast()

  const [modalOpen, setModalOpen] = useState(false)
  const [wiz, setWiz] = useState<WizardState>(defaultWizard)
  const [formError, setFormError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const runs = data?.results ?? []
  const configs = configsData?.results ?? []

  const handleOpenModal = () => {
    setWiz({ ...defaultWizard, name: generateDefaultName() })
    setFormError(null)
    setUploading(false)
    setModalOpen(true)
  }

  const handleNext = () => {
    setFormError(null)
    if (!wiz.name.trim()) { setFormError('Run name is required.'); return }
    if (!wiz.config_id) { setFormError('Please select a config.'); return }
    if (!wiz.sourceType) { setFormError('Please select a source type.'); return }
    setWiz({ ...wiz, step: 2 })
  }

  const handleBack = () => {
    setFormError(null)
    setWiz({ ...wiz, step: 1 })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (wiz.sourceType === 'urls') {
      const urls = wiz.scrape_urls
        .split('\n')
        .map((u) => u.trim())
        .filter(Boolean)
      if (urls.length === 0) { setFormError('Please enter at least one URL.'); return }

      createRunMut.mutate(
        {
          name: wiz.name.trim(),
          config_id: Number(wiz.config_id),
          scrape_urls: urls,
          ...(wiz.skip_steps.length > 0 ? { skip_steps: wiz.skip_steps } : {}),
          ...(wiz.dry_run ? { dry_run: true } : {}),
        },
        {
          onSuccess: () => {
            toast({ title: 'Pipeline run started' })
            setModalOpen(false)
          },
          onError: (err: unknown) => {
            const axErr = err as { response?: { data?: Record<string, unknown> }; message?: string }
            setFormError(String(
              axErr?.response?.data?.error ??
              axErr?.response?.data?.detail ??
              axErr?.message ??
              'Failed to create run'
            ))
          },
        },
      )
    } else {
      // Files flow: create run -> upload files -> signal complete
      if (wiz.files.length === 0) { setFormError('Please select at least one file.'); return }

      try {
        // Step 1: Create the run (use mutateAsync so we can await)
        // For file uploads, always skip scrape
        const fileSkips = [...new Set([...wiz.skip_steps, 'scrape'])]
        const run = await createRunMut.mutateAsync({
          name: wiz.name.trim(),
          config_id: Number(wiz.config_id),
          ...(fileSkips.length > 0 ? { skip_steps: fileSkips } : {}),
          ...(wiz.dry_run ? { dry_run: true } : {}),
        })

        // The external service may return `id` or `job_id`
        const raw = run as unknown as Record<string, unknown>
        const runId = raw.id ?? raw.job_id
        if (!runId) {
          setFormError('Run created but no ID returned. Check the pipeline list.')
          return
        }

        // Step 2: Upload files
        setUploading(true)
        await uploadFiles(String(runId), wiz.files)

        // Step 3: Signal upload complete to trigger pipeline
        await uploadComplete(String(runId))

        toast({ title: 'Files uploaded, pipeline started' })
        setModalOpen(false)
      } catch (err: unknown) {
        const axErr = err as { response?: { data?: Record<string, unknown> }; message?: string }
        setFormError(String(
          axErr?.response?.data?.error ??
          axErr?.response?.data?.detail ??
          axErr?.message ??
          'Upload failed'
        ))
      } finally {
        setUploading(false)
      }
    }
  }

  const handleCancel = (runId: string) => {
    cancelRunMut.mutate(runId, {
      onSuccess: () => toast({ title: 'Pipeline run cancelled' }),
      onError: () => toast({ title: 'Failed to cancel run', variant: 'destructive' }),
    })
  }

  const isBusy = createRunMut.isPending || uploading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-destructive">Error loading pipeline runs</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Triplets Pipeline</h1>
          <p className="text-muted-foreground mt-2">
            Run the full document processing pipeline: scrape, extract, build, enrich, upload, and
            parse.
          </p>
        </div>
        <Button onClick={handleOpenModal}>
          <Plus className="h-4 w-4 mr-2" />
          New pipeline run
        </Button>
      </div>

      {/* Empty state */}
      {runs.length === 0 ? (
        <Card className="border-dashed border-0 shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Workflow className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No pipeline runs yet</h3>
            <p className="text-muted-foreground text-center max-w-md mb-6">
              Create a pipeline run to process documents end-to-end.
            </p>
            <Button onClick={handleOpenModal}>
              <Plus className="h-4 w-4 mr-2" />
              New pipeline run
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Runs table */
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Config</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Steps</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>
                    <Link
                      to={`/app/triplets/${run.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {run.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{run.config_name}</span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={run.status} />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 min-w-[120px]">
                      <span className="text-xs text-muted-foreground">
                        {run.completed_steps}/{run.total_steps} steps
                      </span>
                      <Progress
                        value={
                          run.total_steps > 0
                            ? (run.completed_steps / run.total_steps) * 100
                            : 0
                        }
                        className="h-1.5"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {run.created_at
                        ? formatDistanceToNow(new Date(run.created_at), { addSuffix: true })
                        : '\u2014'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {run.total_time != null ? `${run.total_time.toFixed(0)}s` : '\u2014'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <RunActions run={run} onCancel={handleCancel} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create run wizard dialog */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!isBusy) setModalOpen(open) }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {wiz.step === 1 ? 'New pipeline run' : 'Configure & launch'}
            </DialogTitle>
            <DialogDescription>
              {wiz.step === 1
                ? 'Set up your run and choose how to provide source documents.'
                : wiz.sourceType === 'urls'
                  ? 'Add URLs, select stages, and start the pipeline.'
                  : 'Upload files, select stages, and start the pipeline.'}
            </DialogDescription>
          </DialogHeader>

          {wiz.step === 1 ? (
            /* ── Step 1: Name, Config, Source Type ── */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="run-name">Run name</Label>
                <Input
                  id="run-name"
                  placeholder="e.g. lakewood_full_run"
                  value={wiz.name}
                  onChange={(e) => setWiz({ ...wiz, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Config</Label>
                <Select
                  value={wiz.config_id}
                  onValueChange={(val) => setWiz({ ...wiz, config_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a config" />
                  </SelectTrigger>
                  <SelectContent>
                    {configs.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Source type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <SourceCard
                    icon={<Globe className="h-6 w-6" />}
                    title="URLs"
                    description="Scrape web pages"
                    selected={wiz.sourceType === 'urls'}
                    onClick={() => setWiz({ ...wiz, sourceType: 'urls' })}
                  />
                  <SourceCard
                    icon={<FileUp className="h-6 w-6" />}
                    title="Files"
                    description="Upload PDF, DOCX, etc."
                    selected={wiz.sourceType === 'files'}
                    onClick={() => setWiz({ ...wiz, sourceType: 'files' })}
                  />
                </div>
              </div>

              {formError && <p className="text-sm text-destructive">{formError}</p>}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleNext}>
                  Next
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* ── Step 2: URLs or Files ── */
            <form onSubmit={handleSubmit} className="space-y-4">
              {wiz.sourceType === 'urls' ? (
                <div className="space-y-2">
                  <Label htmlFor="scrape_urls">URLs (one per line)</Label>
                  <Textarea
                    id="scrape_urls"
                    placeholder={"https://example.com/docs\nhttps://example.com/about"}
                    rows={6}
                    value={wiz.scrape_urls}
                    onChange={(e) => setWiz({ ...wiz, scrape_urls: e.target.value })}
                    disabled={isBusy}
                  />
                  <p className="text-xs text-muted-foreground">
                    {wiz.scrape_urls.split('\n').filter((u) => u.trim()).length} URL(s) entered
                  </p>
                </div>
              ) : (
                <FileDropZone
                  files={wiz.files}
                  disabled={isBusy}
                  onAdd={(newFiles) =>
                    setWiz({ ...wiz, files: [...wiz.files, ...newFiles] })
                  }
                  onRemove={(idx) =>
                    setWiz({ ...wiz, files: wiz.files.filter((_, i) => i !== idx) })
                  }
                />
              )}

              {/* Pipeline Stages */}
              <div className="space-y-2 rounded-md border p-3 bg-muted/30">
                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Pipeline Stages
                </Label>
                {STAGE_GROUPS.map((group) => {
                  if (group.hideForFiles && wiz.sourceType === 'files') return null
                  const visibleStages = group.stages
                  return (
                    <div key={group.label} className="space-y-1">
                      <span className="text-[11px] font-medium text-muted-foreground">{group.label}</span>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {visibleStages.map((stage) => {
                          const enabled = !wiz.skip_steps.includes(stage)
                          return (
                            <label key={stage} className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox
                                checked={enabled}
                                onCheckedChange={(checked) => {
                                  const next = checked
                                    ? wiz.skip_steps.filter((s) => s !== stage)
                                    : [...wiz.skip_steps, stage]
                                  setWiz({ ...wiz, skip_steps: next })
                                }}
                                disabled={isBusy}
                                className="h-3.5 w-3.5"
                              />
                              <span className="text-xs">{STAGE_LABELS[stage] ?? stage}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Dry Run toggle */}
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <Label htmlFor="dry-run" className="text-sm font-medium cursor-pointer">Dry Run</Label>
                  <p className="text-xs text-muted-foreground">Validate without processing</p>
                </div>
                <Switch
                  id="dry-run"
                  checked={wiz.dry_run}
                  onCheckedChange={(checked) => setWiz({ ...wiz, dry_run: checked })}
                  disabled={isBusy}
                />
              </div>

              {formError && <p className="text-sm text-destructive">{formError}</p>}

              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="ghost" onClick={handleBack} disabled={isBusy} className="mr-auto">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <Button type="submit" disabled={isBusy}>
                  {isBusy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {uploading ? 'Uploading...' : 'Start pipeline'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Source type card
// ---------------------------------------------------------------------------

function SourceCard({
  icon,
  title,
  description,
  selected,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  description: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-colors hover:bg-accent ${
        selected
          ? 'border-primary bg-primary/5'
          : 'border-muted'
      }`}
    >
      <div className={selected ? 'text-primary' : 'text-muted-foreground'}>{icon}</div>
      <span className="text-sm font-medium">{title}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// File drop zone
// ---------------------------------------------------------------------------

function FileDropZone({
  files,
  disabled,
  onAdd,
  onRemove,
}: {
  files: File[]
  disabled: boolean
  onAdd: (files: File[]) => void
  onRemove: (index: number) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      if (disabled) return
      const dropped = Array.from(e.dataTransfer.files)
      if (dropped.length) onAdd(dropped)
    },
    [disabled, onAdd],
  )

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? [])
    if (selected.length) onAdd(selected)
    e.target.value = ''
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-3">
      <Label>Files</Label>
      {/* Drop area */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors ${
          dragOver
            ? 'border-primary bg-primary/5'
            : 'border-muted hover:border-muted-foreground/40'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <Upload className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Click to browse</span> or drag and drop
        </p>
        <p className="text-xs text-muted-foreground">
          PDF, DOCX, XLSX, CSV, TXT, MD, JSON, HTML, XML
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{files.length} file(s) selected</p>
          <div className="max-h-40 overflow-auto rounded-md border divide-y">
            {files.map((file, i) => (
              <div key={`${file.name}-${i}`} className="flex items-center justify-between px-3 py-1.5 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <FileUp className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                  <span className="truncate">{file.name}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">{formatSize(file.size)}</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRemove(i) }}
                  disabled={disabled}
                  className="text-muted-foreground hover:text-destructive ml-2 flex-shrink-0"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row actions dropdown
// ---------------------------------------------------------------------------

function RunActions({
  run,
  onCancel,
}: {
  run: PipelineRunSummary
  onCancel: (id: string) => void
}) {
  const canCancel = run.status === 'pending' || run.status === 'running'
  if (!canCancel) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onCancel(run.id)}>
          <XCircle className="h-4 w-4 mr-2" />
          Cancel run
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
